import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  getTheme, listThemeTokens, listThemeVersions, saveThemeDraft, publishTheme, themeUsage,
  type CmsTheme, type ThemeTokenSpec, type ThemeUsage, type ThemeVersion,
} from '../../lib/cmsAdmin';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { themeToStyle } from '../../cms/ThemeProvider';
import './AdminPages.css';
import './AdminCms.css';

/**
 * The theme editor.
 *
 * ── NOT THE BLOCK EDITOR ─────────────────────────────────────────────────────────────
 *
 * §38. A theme has no tree, no children and no ordering: it is a flat set of named
 * values. Reusing the three-region block editor would mean an outline of one node and a
 * palette with nothing in it. This is a form, and the form is GENERATED from the token
 * schema the engine serves — so a token added there gets a control here with no work,
 * and a token removed cannot leave a stale input writing a value the server refuses.
 *
 * ── THE PREVIEW IS THE REAL MECHANISM ────────────────────────────────────────────────
 *
 * §39. The sample below is styled by the same themeToStyle() that styles a live page,
 * writing the same CSS custom properties that the real blocks read. It is a small set of
 * representative pieces rather than a fake website builder — enough to judge contrast and
 * shape, and honest about being a sample.
 */
export function ThemeEditor() {
  const { themeId } = useParams<{ themeId: string }>();

  const [theme, setTheme] = useState<CmsTheme | null>(null);
  const [schema, setSchema] = useState<ThemeTokenSpec[]>([]);
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [savedTokens, setSavedTokens] = useState<Record<string, string>>({});
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  const [versions, setVersions] = useState<ThemeVersion[]>([]);
  const [usage, setUsage] = useState<ThemeUsage | null>(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    if (!themeId) return;
    setLoading(true);
    try {
      const [fetched, tokenSchema, history] = await Promise.all([
        getTheme(themeId), listThemeTokens(), listThemeVersions(themeId),
      ]);
      setTheme(fetched);
      setSchema(tokenSchema);
      setVersions(history);

      const draft = history.find((v) => v.state === 'draft');
      const published = history.find((v) => v.state === 'published');
      setPublishedVersion(published?.version ?? null);

      // With no draft, editing starts from what is live — and with nothing live, from
      // Voleto's own values, so a new theme opens looking like the product rather than
      // like an empty form.
      const start = draft?.tokens ?? published?.tokens ?? {};
      setTokens({ ...start });
      setSavedTokens({ ...start });
      setSavedVersion(draft?.version ?? null);

      themeUsage(themeId).then(setUsage).catch(() => setUsage(null));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'This theme could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [themeId]);

  useEffect(() => { void load(); }, [load]);

  // Dirtiness is COMPUTED, never tracked as a flag. A flag drifts the moment anything
  // sets state without remembering to clear it.
  const dirty = useMemo(
    () => JSON.stringify(tokens) !== JSON.stringify(savedTokens),
    [tokens, savedTokens],
  );

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** The bundle as it would render: the platform defaults with this theme laid over. */
  const effective = useMemo(() => {
    const out: Record<string, string> = {};
    for (const spec of schema) out[spec.name] = tokens[spec.name] ?? spec.platformDefault;
    return out;
  }, [schema, tokens]);

  const groups = useMemo(() => {
    const byGroup: Record<string, ThemeTokenSpec[]> = {};
    for (const spec of schema) (byGroup[spec.group] ??= []).push(spec);
    return byGroup;
  }, [schema]);

  usePageChrome({
    breadcrumb: [
      { label: 'Content & design' },
      { label: 'Themes', to: '/admin/content/themes' },
      { label: theme?.name ?? 'Theme' },
    ],
  }, [theme?.name]);

  function setToken(name: string, value: string | null) {
    setTokens((current) => {
      const next = { ...current };
      // Clearing a token is not setting it to the default value: it REMOVES the
      // override, so the token keeps inheriting whatever the layer beneath supplies. A
      // theme that pinned every value would stop tracking a platform change forever.
      if (value === null) delete next[name];
      else next[name] = value;
      return next;
    });
  }

  async function save() {
    if (!themeId) return;
    setBusy(true);
    setError(null);
    try {
      const version = await saveThemeDraft(themeId, {
        tokens, expectedVersion: savedVersion,
      });
      setSavedTokens({ ...tokens });
      setSavedVersion(version.version);
      setFlash(`Draft saved as v${version.version}. Nothing is live until you publish.`);
      listThemeVersions(themeId).then(setVersions).catch(() => {});
    } catch (err) {
      setError(conflictMessage(err)
        ?? (err instanceof ApiError ? err.message : 'The draft could not be saved.'));
    } finally { setBusy(false); }
  }

  async function publish() {
    if (!themeId) return;
    setBusy(true);
    try {
      const version = await publishTheme(themeId, savedVersion);
      setConfirming(false);
      setFlash(`Published v${version.version}. Every page resolving this theme is using it now.`);
      await load();
    } catch (err) {
      setConfirming(false);
      setError(conflictMessage(err)
        ?? (err instanceof ApiError ? err.message : 'The publish was refused.'));
    } finally { setBusy(false); }
  }

  function restore(version: ThemeVersion) {
    setTokens({ ...version.tokens });
    setShowHistory(false);
    setFlash(`v${version.version} loaded into the editor. Save it to keep it, publish it to `
      + 'make it live.');
  }

  if (loading) return <p className="adm-lede">Loading the theme…</p>;
  if (error && !theme) return <div className="alert alert--error" role="alert">{error}</div>;
  if (!theme) return null;

  return (
    <div className="cms-editor">
      <header className="cms-bar">
        <div className="cms-bar__id">
          <h1>{theme.name}</h1>
          <p className="cms-bar__meta">
            <span className="badge">{theme.scope === 'platform' ? 'Platform' : 'Organization'}</span>
            {theme.isDefault && <span className="badge badge--ok">Default</span>}
            {publishedVersion
              ? <span className="badge badge--ok">Live: v{publishedVersion}</span>
              : <span className="badge badge--pending">Never published</span>}
            {savedVersion && <span className="badge badge--pending">Draft v{savedVersion}</span>}
            {dirty && <span className="badge badge--error">Unsaved changes</span>}
          </p>
        </div>

        <div className="cms-bar__actions">
          <button type="button" className="button button--ghost"
            onClick={() => setShowHistory(true)}>History</button>
          <button type="button" className="button button--secondary"
            disabled={!dirty || busy} onClick={() => { void save(); }}>
            {busy ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={busy || dirty || savedVersion == null}
            title={dirty ? 'Save the draft before publishing it.'
              : savedVersion == null ? 'There is no draft to publish.' : undefined}
            onClick={() => setConfirming(true)}
          >
            Publish
          </button>
        </div>
      </header>

      {flash && (
        <div className="adm-flash" role="status">
          {flash}
          <button type="button" className="cms-icon" onClick={() => setFlash(null)}
            aria-label="Dismiss">×</button>
        </div>
      )}
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <div className="cms-theme-editor">
        <div className="cms-theme-editor__fields">
          {Object.entries(groups).map(([group, specs]) => (
            <section key={group} className="cms-inspector__group">
              <h2 className="cms-region__title">{group}</h2>
              {specs.map((spec) => (
                <TokenField
                  key={spec.name}
                  spec={spec}
                  value={tokens[spec.name] ?? null}
                  onChange={(v) => setToken(spec.name, v)}
                />
              ))}
            </section>
          ))}
          <p className="adm-hint">
            Colours are hex values. Everything else is chosen from a list. There is no CSS
            field: a theme is data, so nothing set here can execute or reach a property
            Voleto did not offer.
          </p>
        </div>

        <div className="cms-theme-editor__preview">
          <h2 className="cms-region__title">Preview</h2>
          <ThemeSample tokens={effective} />
          <p className="adm-hint">
            A sample, not a page. It uses the same CSS variables the real blocks read, so
            what you judge here is what a page will do.
          </p>
        </div>
      </div>

      {confirming && (
        <PublishThemeDialog
          theme={theme} usage={usage} busy={busy}
          onPublish={publish} onClose={() => setConfirming(false)}
        />
      )}

      {showHistory && (
        <ThemeHistory
          versions={versions} currentDraftVersion={savedVersion}
          onRestore={restore} onClose={() => setShowHistory(false)}
        />
      )}

      <p className="cms-footnote">
        <Link to="/admin/content/themes">&larr; Back to themes</Link>
      </p>
    </div>
  );
}

/**
 * One generated control.
 *
 * The control is chosen by the token's KIND, which the engine declares. Nothing here
 * knows what a radius is.
 */
function TokenField({
  spec, value, onChange,
}: { spec: ThemeTokenSpec; value: string | null; onChange: (value: string | null) => void }) {
  const effective = value ?? spec.platformDefault;
  const inherited = value === null;

  return (
    <div className="field cms-token">
      <span className="field__label">
        {spec.label}
        {inherited && <span className="badge badge--muted">Inherited</span>}
      </span>

      {spec.kind === 'color' ? (
        <div className="cms-token__color">
          <input
            type="color"
            value={effective.slice(0, 7)}
            aria-label={`${spec.label} colour`}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
          />
          <input
            className="field__input"
            value={effective}
            spellCheck={false}
            aria-label={`${spec.label} hex value`}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
          />
        </div>
      ) : (
        <select className="field__input" value={effective}
          onChange={(e) => onChange(e.target.value)}>
          {(spec.values ?? []).map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      )}

      {spec.help && <span className="field__help">{spec.help}</span>}
      {!inherited && (
        <button type="button" className="cms-token__reset" onClick={() => onChange(null)}>
          Reset to inherited ({spec.platformDefault})
        </button>
      )}
    </div>
  );
}

/**
 * The representative sample.
 *
 * §39/§62. A heading, body text, a surface, a border, a primary button and a
 * ticket-shaped card — the pieces whose contrast and shape actually matter — plus a
 * footer-like strip so it is visible that a global section inherits the host's theme
 * rather than carrying one of its own.
 */
function ThemeSample({ tokens }: { tokens: Record<string, string> }) {
  const style = themeToStyle(tokens);
  return (
    <div className="cms-sample" style={style}>
      <div className="cms-sample__page">
        <h3 className="cms-sample__heading">An evening at the Fillmore</h3>
        <p className="cms-sample__body">
          Doors at seven. This paragraph is here so you can judge body text against the
          page background before anybody has to read a real one.
        </p>

        <div className="cms-sample__card">
          <span className="cms-sample__muted">General admission</span>
          <strong className="cms-sample__price">$35.00</strong>
          <button type="button" className="cms-sample__button">Buy tickets</button>
        </div>

        <div className="cms-sample__footer">
          <span className="cms-sample__muted">
            A global section renders with the host page&rsquo;s theme, not its own.
          </span>
        </div>
      </div>
    </div>
  );
}

function PublishThemeDialog({
  theme, usage, busy, onPublish, onClose,
}: {
  theme: CmsTheme;
  usage: ThemeUsage | null;
  busy: boolean;
  onPublish: () => Promise<void>;
  onClose: () => void;
}) {
  return (
    <div className="cms-modal" role="dialog" aria-modal="true" aria-label="Publish theme">
      <div className="cms-modal__panel">
        <header className="cms-modal__head">
          <h2>Publish &ldquo;{theme.name}&rdquo;</h2>
        </header>

        <div className="cms-impact">
          <h3>What this changes</h3>
          {!usage && <p className="adm-muted">Counting…</p>}
          {usage && (usage.events > 0 || usage.templates + usage.pages + usage.organizations > 0
            ? (
              <ul>
                {usage.organizations > 0 && (
                  <li>
                    It is the default for <strong>{usage.organizations}</strong>{' '}
                    organization{usage.organizations === 1 ? '' : 's'}.
                  </li>
                )}
                {theme.isDefault && theme.scope === 'platform' && (
                  <li>It is the <strong>platform default</strong>, so anything that inherits uses it.</li>
                )}
                {usage.templates > 0 && (
                  <li><strong>{usage.templates}</strong> template{usage.templates === 1 ? '' : 's'} assign it.</li>
                )}
                {usage.pages > 0 && (
                  <li><strong>{usage.pages}</strong> page{usage.pages === 1 ? '' : 's'} assign it.</li>
                )}
                <li>
                  <strong>{usage.events}</strong> live event{usage.events === 1 ? '' : 's'}{' '}
                  currently resolve{usage.events === 1 ? 's' : ''} this theme.
                </li>
              </ul>
            )
            : <p>Nothing resolves this theme yet, so publishing changes nothing a visitor can see.</p>)}
          {usage && (
            <p className="adm-muted">
              Counted across {usage.visibleEvents} live events, excluding test accounts.
            </p>
          )}
        </div>

        <p className="adm-hint">
          The published version becomes live immediately, everywhere it resolves. The
          version you are replacing is kept and can be brought back as a new draft.
        </p>

        <footer className="cms-modal__foot">
          <button type="button" className="button button--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="button button--primary" disabled={busy}
            onClick={() => { void onPublish(); }}>
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ThemeHistory({
  versions, currentDraftVersion, onRestore, onClose,
}: {
  versions: ThemeVersion[];
  currentDraftVersion: number | null;
  onRestore: (version: ThemeVersion) => void;
  onClose: () => void;
}) {
  return (
    <div className="cms-modal" role="dialog" aria-modal="true" aria-label="Theme history">
      <div className="cms-modal__panel cms-modal__panel--wide">
        <header className="cms-modal__head">
          <h2>Version history</h2>
          <button type="button" className="button button--ghost adm-btn-sm" onClick={onClose}>
            Close
          </button>
        </header>

        <table className="adm-table cms-versions">
          <thead>
            <tr><th>Version</th><th>State</th><th>Saved</th><th>By</th><th /></tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id}>
                <td>v{v.version}</td>
                <td>
                  {v.state === 'published' && <span className="badge badge--ok">Live</span>}
                  {v.state === 'draft' && <span className="badge badge--pending">Draft</span>}
                  {v.state !== 'published' && v.state !== 'draft' && <span className="badge">{v.state}</span>}
                </td>
                <td>{new Date(v.createdAt).toLocaleString()}</td>
                <td>{v.createdBy ?? '—'}</td>
                <td>
                  {v.version !== currentDraftVersion && (
                    <button type="button" className="button button--ghost adm-btn-sm"
                      onClick={() => onRestore(v)}>
                      Restore into draft
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="adm-hint">
          Restoring copies that version into the current draft. Nothing changes for
          visitors until the draft is published.
        </p>
      </div>
    </div>
  );
}

function conflictMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  return 'Somebody else has saved this theme since you opened it. Reload to see their '
    + 'version. Your changes are still on screen, so copy anything you need first.';
}
